import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase.js";
import { toast } from "react-toastify";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "obligations", label: "Legal checklist" },
  { id: "meetings", label: "Meetings" },
  { id: "elections", label: "Elections" },
  { id: "settings", label: "Settings" },
];

const STATUS_LABELS = {
  upcoming: "Upcoming",
  due_soon: "Due soon",
  overdue: "Overdue",
  completed: "Completed",
  waived: "Waived",
};

const MEETING_TYPE_LABELS = {
  annual: "Annual member",
  regular_board: "Regular board",
  special: "Special",
  budget: "Budget",
  executive_session: "Executive session",
};

const ELECTION_STATUS_LABELS = {
  planning: "Planning",
  nominations_open: "Nominations open",
  voting: "Voting",
  certified: "Certified",
  cancelled: "Cancelled",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function StatCard({ label, value, hint, tone }) {
  return (
    <div className={`compliance-stat-card ${tone ? `compliance-stat-card--${tone}` : ""}`}>
      <p className="compliance-stat-label">{label}</p>
      <p className="compliance-stat-value">{value}</p>
      {hint ? <p className="compliance-stat-hint">{hint}</p> : null}
    </div>
  );
}

function AdminCompliancePage({ isAdmin, isMobile }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disclaimer, setDisclaimer] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [obligations, setObligations] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [elections, setElections] = useState([]);
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showObligationForm, setShowObligationForm] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [showElectionForm, setShowElectionForm] = useState(false);
  const [obligationForm, setObligationForm] = useState({
    title: "",
    category: "meetings",
    description: "",
    dueDate: "",
    fiscalYear: new Date().getFullYear(),
    notes: "",
  });
  const [meetingForm, setMeetingForm] = useState({
    type: "regular_board",
    title: "",
    scheduledAt: "",
    location: "",
    noticeSentAt: "",
    noticeDaysRequired: 14,
    agendaUrl: "",
    openToOwners: true,
    notes: "",
  });
  const [electionForm, setElectionForm] = useState({
    title: "",
    electionDate: "",
    nominationDeadline: "",
    ballotMethod: "in_person",
    quorumRequired: "",
    status: "planning",
    positionsText: "",
    notes: "",
  });
  const [configForm, setConfigForm] = useState({
    state: "NC",
    fiscalYearStartMonth: 1,
    defaultMeetingNoticeDays: 14,
    electionCycleYears: 1,
    communityType: "planned_community",
  });

  const loadTemplates = useCallback(async () => {
    const fn = httpsCallable(functions, "getComplianceTemplates");
    const { data } = await fn();
    setTemplates(data.templates || []);
    setDisclaimer(data.disclaimer || "");
    return data;
  }, []);

  const loadDashboard = useCallback(async () => {
    const fn = httpsCallable(functions, "getComplianceDashboard");
    const { data } = await fn();
    setDashboard(data);
    setDisclaimer(data.disclaimer || "");
    return data;
  }, []);

  const loadObligations = useCallback(async (fiscalYear) => {
    const fn = httpsCallable(functions, "listComplianceObligations");
    const { data } = await fn(fiscalYear ? { fiscalYear } : {});
    setObligations(data.obligations || []);
    return data.obligations || [];
  }, []);

  const loadMeetings = useCallback(async () => {
    const fn = httpsCallable(functions, "listMeetings");
    const { data } = await fn();
    setMeetings(data.meetings || []);
    return data.meetings || [];
  }, []);

  const loadElections = useCallback(async () => {
    const fn = httpsCallable(functions, "listElections");
    const { data } = await fn();
    setElections(data.elections || []);
    return data.elections || [];
  }, []);

  const loadConfig = useCallback(async () => {
    const fn = httpsCallable(functions, "getComplianceConfig");
    const { data } = await fn();
    setConfig(data.config);
    setConfigForm({
      state: data.config?.state || "NC",
      fiscalYearStartMonth: data.config?.fiscalYearStartMonth || 1,
      defaultMeetingNoticeDays: data.config?.defaultMeetingNoticeDays || 14,
      electionCycleYears: data.config?.electionCycleYears || 1,
      communityType: data.config?.communityType || "planned_community",
    });
    setDisclaimer(data.disclaimer || "");
    return data.config;
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const dash = await loadDashboard();
      await Promise.all([
        loadObligations(dash?.fiscalYear),
        loadMeetings(),
        loadElections(),
        loadConfig(),
        loadTemplates(),
      ]);
    } catch (error) {
      toast.error(`Failed to load compliance data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [loadConfig, loadDashboard, loadElections, loadMeetings, loadObligations, loadTemplates]);

  useEffect(() => {
    document.title = "Governance & Compliance | Nantahala Mountain Resort";
    return () => {
      document.title = "NMR Community";
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    refreshAll();
  }, [isAdmin, refreshAll]);

  const handleGenerateObligations = async () => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "generateComplianceObligations");
      const year = dashboard?.fiscalYear || new Date().getFullYear();
      const { data } = await fn({ fiscalYear: year });
      toast.success(`Generated ${data.created} obligation(s) for FY ${data.fiscalYear}`);
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to generate obligations");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveObligation = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "upsertComplianceObligation");
      await fn(obligationForm);
      toast.success("Obligation saved");
      setShowObligationForm(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to save obligation");
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteObligation = async (obligationId, waived = false) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "completeComplianceObligation");
      await fn({ obligationId, waived });
      toast.success(waived ? "Obligation waived" : "Marked complete");
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to update obligation");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMeeting = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "upsertMeeting");
      await fn(meetingForm);
      toast.success("Meeting saved");
      setShowMeetingForm(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to save meeting");
    } finally {
      setSaving(false);
    }
  };

  const handleMeetingHeld = async (meetingId) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "recordMeetingHeld");
      await fn({ meetingId, quorumMet: true, minutesPublished: false });
      toast.success("Meeting marked as held");
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to update meeting");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveElection = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const positions = electionForm.positionsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [title, termYears = "2"] = line.split("|").map((s) => s.trim());
          return { title, termYears: Number(termYears) || 2 };
        });
      const fn = httpsCallable(functions, "upsertElection");
      await fn({
        title: electionForm.title,
        electionDate: electionForm.electionDate,
        nominationDeadline: electionForm.nominationDeadline || null,
        ballotMethod: electionForm.ballotMethod,
        quorumRequired: electionForm.quorumRequired,
        status: electionForm.status,
        positions,
        notes: electionForm.notes,
      });
      toast.success("Election saved");
      setShowElectionForm(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to save election");
    } finally {
      setSaving(false);
    }
  };

  const handleCertifyElection = async (electionId) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "certifyElection");
      await fn({ electionId, results: [] });
      toast.success("Election certified");
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to certify election");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "updateComplianceConfig");
      const { data } = await fn(configForm);
      setConfig(data.config);
      toast.success("Settings saved");
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="page-container">
        <div className="docs-card">
          <h1>Access denied</h1>
          <p>Governance compliance tools are restricted to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-compliance-page ${isMobile ? "admin-compliance-page--mobile" : ""}`}>
      <style>{`
        .admin-compliance-page { padding: ${isMobile ? "1rem" : "2rem"}; max-width: 1200px; margin: 0 auto; }
        .compliance-header { margin-bottom: 1rem; }
        .compliance-title { font-size: ${isMobile ? "1.35rem" : "1.75rem"}; margin: 0 0 0.35rem; color: #1a3c34; }
        .compliance-subtitle { margin: 0; color: #4b5563; line-height: 1.5; }
        .compliance-disclaimer { margin-top: 0.75rem; padding: 0.65rem 0.85rem; background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; font-size: 0.85rem; color: #92400e; }
        .compliance-tabs { display: flex; flex-wrap: wrap; gap: 0.5rem; margin: 1rem 0; }
        .compliance-tab { border: 1px solid #cbd5e1; background: #fff; color: #1a3c34; padding: 0.45rem 0.85rem; border-radius: 999px; cursor: pointer; }
        .compliance-tab--active { background: #1a3c34; color: #fff; border-color: #1a3c34; }
        .compliance-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
        .compliance-stat-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.85rem; }
        .compliance-stat-card--warn { border-color: #fcd34d; background: #fffbeb; }
        .compliance-stat-card--danger { border-color: #fca5a5; background: #fef2f2; }
        .compliance-stat-label { margin: 0; font-size: 0.75rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
        .compliance-stat-value { margin: 0.35rem 0 0; font-size: 1.35rem; font-weight: 700; color: #0f172a; }
        .compliance-stat-hint { margin: 0.25rem 0 0; font-size: 0.8rem; color: #64748b; }
        .compliance-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 1rem; margin-bottom: 1rem; }
        .compliance-toolbar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin-bottom: 0.75rem; }
        .compliance-btn { background: #1a3c34; color: #fff; border: none; border-radius: 8px; padding: 0.5rem 0.9rem; cursor: pointer; }
        .compliance-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .compliance-btn--secondary { background: #fff; color: #1a3c34; border: 1px solid #94a3b8; }
        .compliance-btn--small { padding: 0.3rem 0.55rem; font-size: 0.8rem; }
        .compliance-table-wrap { overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 10px; }
        .compliance-table { width: 100%; border-collapse: collapse; min-width: 640px; }
        .compliance-table th, .compliance-table td { padding: 0.55rem 0.65rem; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 0.9rem; vertical-align: top; }
        .compliance-table th { background: #1a3c34; color: #fff; }
        .compliance-table tr:nth-child(even) { background: #f8fafc; }
        .compliance-badge { display: inline-block; padding: 0.15rem 0.45rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600; }
        .compliance-badge--overdue { background: #fee2e2; color: #991b1b; }
        .compliance-badge--due_soon { background: #fef3c7; color: #92400e; }
        .compliance-badge--upcoming { background: #e2e8f0; color: #334155; }
        .compliance-badge--completed { background: #dcfce7; color: #166534; }
        .compliance-badge--waived { background: #f3e8ff; color: #6b21a8; }
        .compliance-form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.75rem; }
        .compliance-form-group label { display: block; font-size: 0.85rem; margin-bottom: 0.25rem; color: #334155; }
        .compliance-form-group input, .compliance-form-group select, .compliance-form-group textarea {
          width: 100%; padding: 0.45rem 0.55rem; border: 1px solid #cbd5e1; border-radius: 8px;
        }
        .compliance-form-group--full { grid-column: 1 / -1; }
        .compliance-modal-overlay {
          position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 1200;
          display: flex; align-items: center; justify-content: center; padding: 1rem;
        }
        .compliance-modal { background: #fff; border-radius: 12px; padding: 1rem; width: min(560px, 100%); max-height: 90vh; overflow-y: auto; }
        .compliance-actions { display: flex; flex-wrap: wrap; gap: 0.35rem; }
        .compliance-template-list { font-size: 0.85rem; color: #475569; margin: 0.5rem 0 0; padding-left: 1.1rem; }
      `}</style>

      <header className="compliance-header">
        <h1 className="compliance-title">Governance & Compliance</h1>
        <p className="compliance-subtitle">
          Track meetings, elections, notices, and recurring legal obligations. Owners see upcoming meetings on the{" "}
          <Link to="/compliance-calendar">Governance Calendar</Link>. Link certified elections to{" "}
          <Link to="/admin-polls">Polls</Link> when using online ballots.
        </p>
        {disclaimer ? <p className="compliance-disclaimer">{disclaimer}</p> : null}
      </header>

      <div className="compliance-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`compliance-tab ${activeTab === tab.id ? "compliance-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        <button type="button" className="compliance-btn compliance-btn--secondary" disabled={loading} onClick={refreshAll}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {loading && !dashboard ? (
        <p>Loading compliance data…</p>
      ) : (
        <>
          {activeTab === "overview" && dashboard ? (
            <section className="compliance-panel">
              <div className="compliance-stats">
                <StatCard label="Compliance health" value={`${dashboard.healthScore}%`} hint={`FY ${dashboard.fiscalYear}`} />
                <StatCard
                  label="Overdue"
                  value={dashboard.counts.overdue}
                  hint="Requires immediate attention"
                  tone={dashboard.counts.overdue ? "danger" : undefined}
                />
                <StatCard
                  label="Due in 30 days"
                  value={dashboard.counts.dueSoon}
                  tone={dashboard.counts.dueSoon ? "warn" : undefined}
                />
                <StatCard label="Upcoming meetings" value={dashboard.counts.upcomingMeetings} />
                <StatCard
                  label="Notice gaps"
                  value={dashboard.counts.noticeGaps}
                  hint="Notice sent too late"
                  tone={dashboard.counts.noticeGaps ? "warn" : undefined}
                />
                <StatCard label="Active elections" value={dashboard.counts.activeElections} />
              </div>

              {dashboard.overdue?.length ? (
                <>
                  <h3>Overdue obligations</h3>
                  <div className="compliance-table-wrap">
                    <table className="compliance-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Due</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.overdue.map((row) => (
                          <tr key={row.id}>
                            <td>{row.title}</td>
                            <td>{formatDate(row.dueDate)}</td>
                            <td>{row.category}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}

              {dashboard.noticeGaps?.length ? (
                <>
                  <h3>Meetings with insufficient notice</h3>
                  <ul>
                    {dashboard.noticeGaps.map((m) => (
                      <li key={m.id}>
                        {m.title} — scheduled {formatDate(m.scheduledAt)} (requires {m.noticeDaysRequired} days notice)
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
          ) : null}

          {activeTab === "obligations" ? (
            <section className="compliance-panel">
              <div className="compliance-toolbar">
                <button type="button" className="compliance-btn" onClick={handleGenerateObligations} disabled={saving}>
                  Generate FY checklist
                </button>
                <button
                  type="button"
                  className="compliance-btn compliance-btn--secondary"
                  onClick={() => setShowObligationForm(true)}
                >
                  Add custom item
                </button>
              </div>
              <p className="compliance-subtitle">
                Templates cover annual meetings, elections, budget, insurance, taxes, and records — tuned for NC by default.
              </p>
              <div className="compliance-table-wrap">
                <table className="compliance-table">
                  <thead>
                    <tr>
                      <th>Requirement</th>
                      <th>Due</th>
                      <th>Status</th>
                      <th>Reference</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obligations.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          No obligations yet. Click <strong>Generate FY checklist</strong> to seed legal requirements.
                        </td>
                      </tr>
                    ) : (
                      obligations.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <strong>{row.title}</strong>
                            {row.description ? <div style={{ fontSize: "0.8rem", color: "#64748b" }}>{row.description}</div> : null}
                          </td>
                          <td>{formatDate(row.dueDate)}</td>
                          <td>
                            <span className={`compliance-badge compliance-badge--${row.status}`}>
                              {STATUS_LABELS[row.status] || row.status}
                            </span>
                          </td>
                          <td style={{ fontSize: "0.8rem" }}>{row.legalReference || "—"}</td>
                          <td>
                            {row.status !== "completed" && row.status !== "waived" ? (
                              <div className="compliance-actions">
                                <button
                                  type="button"
                                  className="compliance-btn compliance-btn--small"
                                  disabled={saving}
                                  onClick={() => handleCompleteObligation(row.id)}
                                >
                                  Complete
                                </button>
                                <button
                                  type="button"
                                  className="compliance-btn compliance-btn--secondary compliance-btn--small"
                                  disabled={saving}
                                  onClick={() => handleCompleteObligation(row.id, true)}
                                >
                                  Waive
                                </button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {templates.length ? (
                <details style={{ marginTop: "1rem" }}>
                  <summary>Included template library ({templates.length} items)</summary>
                  <ul className="compliance-template-list">
                    {templates.map((t) => (
                      <li key={t.id}>
                        {t.title} — {t.category}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </section>
          ) : null}

          {activeTab === "meetings" ? (
            <section className="compliance-panel">
              <div className="compliance-toolbar">
                <button type="button" className="compliance-btn" onClick={() => setShowMeetingForm(true)}>
                  Schedule meeting
                </button>
                <Link to="/meeting-minutes" className="compliance-btn compliance-btn--secondary">
                  Meeting minutes library
                </Link>
              </div>
              <div className="compliance-table-wrap">
                <table className="compliance-table">
                  <thead>
                    <tr>
                      <th>Meeting</th>
                      <th>When</th>
                      <th>Notice</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meetings.length === 0 ? (
                      <tr>
                        <td colSpan={5}>No meetings scheduled. Add annual, board, and special sessions with notice dates.</td>
                      </tr>
                    ) : (
                      meetings.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <strong>{row.title}</strong>
                            <div style={{ fontSize: "0.8rem" }}>{MEETING_TYPE_LABELS[row.type] || row.type}</div>
                            {row.location ? <div style={{ fontSize: "0.8rem" }}>{row.location}</div> : null}
                          </td>
                          <td>{formatDate(row.scheduledAt)}</td>
                          <td>
                            {row.noticeSentAt ? (
                              <>
                                Sent {formatDate(row.noticeSentAt)}
                                {row.noticeCompliant === false ? (
                                  <span className="compliance-badge compliance-badge--overdue"> Short notice</span>
                                ) : row.noticeCompliant ? (
                                  <span className="compliance-badge compliance-badge--completed"> OK</span>
                                ) : null}
                              </>
                            ) : (
                              <span className="compliance-badge compliance-badge--due_soon">Not logged</span>
                            )}
                          </td>
                          <td>{row.status}</td>
                          <td>
                            {row.status === "scheduled" ? (
                              <button
                                type="button"
                                className="compliance-btn compliance-btn--small"
                                disabled={saving}
                                onClick={() => handleMeetingHeld(row.id)}
                              >
                                Mark held
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {activeTab === "elections" ? (
            <section className="compliance-panel">
              <div className="compliance-toolbar">
                <button type="button" className="compliance-btn" onClick={() => setShowElectionForm(true)}>
                  Plan election
                </button>
                <Link to="/board-members" className="compliance-btn compliance-btn--secondary">
                  Board roster
                </Link>
              </div>
              <div className="compliance-table-wrap">
                <table className="compliance-table">
                  <thead>
                    <tr>
                      <th>Election</th>
                      <th>Date</th>
                      <th>Nominations due</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {elections.length === 0 ? (
                      <tr>
                        <td colSpan={5}>No elections on file. Track director elections and link to polls for online voting.</td>
                      </tr>
                    ) : (
                      elections.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <strong>{row.title}</strong>
                            {row.positions?.length ? (
                              <div style={{ fontSize: "0.8rem" }}>
                                {row.positions.map((p) => p.title).join(", ")}
                              </div>
                            ) : null}
                          </td>
                          <td>{formatDate(row.electionDate)}</td>
                          <td>{formatDate(row.nominationDeadline)}</td>
                          <td>{ELECTION_STATUS_LABELS[row.status] || row.status}</td>
                          <td>
                            {row.status === "voting" ? (
                              <button
                                type="button"
                                className="compliance-btn compliance-btn--small"
                                disabled={saving}
                                onClick={() => handleCertifyElection(row.id)}
                              >
                                Certify
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {activeTab === "settings" ? (
            <section className="compliance-panel">
              <form onSubmit={handleSaveConfig}>
                <div className="compliance-form-grid">
                  <div className="compliance-form-group">
                    <label>State</label>
                    <input
                      value={configForm.state}
                      onChange={(e) => setConfigForm({ ...configForm, state: e.target.value })}
                      maxLength={2}
                    />
                  </div>
                  <div className="compliance-form-group">
                    <label>Fiscal year starts (month)</label>
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={configForm.fiscalYearStartMonth}
                      onChange={(e) =>
                        setConfigForm({ ...configForm, fiscalYearStartMonth: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div className="compliance-form-group">
                    <label>Default meeting notice (days)</label>
                    <input
                      type="number"
                      min={0}
                      value={configForm.defaultMeetingNoticeDays}
                      onChange={(e) =>
                        setConfigForm({ ...configForm, defaultMeetingNoticeDays: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div className="compliance-form-group">
                    <label>Election cycle (years)</label>
                    <input
                      type="number"
                      min={1}
                      value={configForm.electionCycleYears}
                      onChange={(e) =>
                        setConfigForm({ ...configForm, electionCycleYears: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div className="compliance-form-group compliance-form-group--full">
                    <label>Community type</label>
                    <select
                      value={configForm.communityType}
                      onChange={(e) => setConfigForm({ ...configForm, communityType: e.target.value })}
                    >
                      <option value="planned_community">Planned community (NC 47F)</option>
                      <option value="condominium">Condominium (NC 47C)</option>
                      <option value="single_family">Single-family HOA</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="compliance-toolbar" style={{ marginTop: "0.75rem" }}>
                  <button type="submit" className="compliance-btn" disabled={saving}>
                    {saving ? "Saving…" : "Save settings"}
                  </button>
                </div>
              </form>
              {config ? (
                <p style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "1rem" }}>
                  Current: {config.state} · FY starts month {config.fiscalYearStartMonth} ·{" "}
                  {config.defaultMeetingNoticeDays}-day meeting notice default
                </p>
              ) : null}
            </section>
          ) : null}
        </>
      )}

      {showObligationForm ? (
        <div className="compliance-modal-overlay" onClick={() => setShowObligationForm(false)}>
          <div className="compliance-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add compliance item</h2>
            <form onSubmit={handleSaveObligation}>
              <div className="compliance-form-grid">
                <div className="compliance-form-group compliance-form-group--full">
                  <label>Title</label>
                  <input
                    value={obligationForm.title}
                    onChange={(e) => setObligationForm({ ...obligationForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Category</label>
                  <select
                    value={obligationForm.category}
                    onChange={(e) => setObligationForm({ ...obligationForm, category: e.target.value })}
                  >
                    <option value="meetings">Meetings</option>
                    <option value="elections">Elections</option>
                    <option value="financial">Financial</option>
                    <option value="insurance">Insurance</option>
                    <option value="records">Records</option>
                    <option value="taxes">Taxes</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="compliance-form-group">
                  <label>Due date</label>
                  <input
                    type="datetime-local"
                    value={obligationForm.dueDate}
                    onChange={(e) => setObligationForm({ ...obligationForm, dueDate: e.target.value })}
                    required
                  />
                </div>
                <div className="compliance-form-group compliance-form-group--full">
                  <label>Description</label>
                  <textarea
                    rows={2}
                    value={obligationForm.description}
                    onChange={(e) => setObligationForm({ ...obligationForm, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="compliance-toolbar" style={{ marginTop: "0.75rem" }}>
                <button type="submit" className="compliance-btn" disabled={saving}>Save</button>
                <button type="button" className="compliance-btn compliance-btn--secondary" onClick={() => setShowObligationForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showMeetingForm ? (
        <div className="compliance-modal-overlay" onClick={() => setShowMeetingForm(false)}>
          <div className="compliance-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Schedule meeting</h2>
            <form onSubmit={handleSaveMeeting}>
              <div className="compliance-form-grid">
                <div className="compliance-form-group">
                  <label>Type</label>
                  <select
                    value={meetingForm.type}
                    onChange={(e) => setMeetingForm({ ...meetingForm, type: e.target.value })}
                  >
                    {Object.entries(MEETING_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="compliance-form-group">
                  <label>Open to owners</label>
                  <select
                    value={meetingForm.openToOwners ? "yes" : "no"}
                    onChange={(e) => setMeetingForm({ ...meetingForm, openToOwners: e.target.value === "yes" })}
                  >
                    <option value="yes">Yes — show on owner calendar</option>
                    <option value="no">No — board only</option>
                  </select>
                </div>
                <div className="compliance-form-group compliance-form-group--full">
                  <label>Title</label>
                  <input
                    value={meetingForm.title}
                    onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Scheduled</label>
                  <input
                    type="datetime-local"
                    value={meetingForm.scheduledAt}
                    onChange={(e) => setMeetingForm({ ...meetingForm, scheduledAt: e.target.value })}
                    required
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Notice sent</label>
                  <input
                    type="datetime-local"
                    value={meetingForm.noticeSentAt}
                    onChange={(e) => setMeetingForm({ ...meetingForm, noticeSentAt: e.target.value })}
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Notice required (days)</label>
                  <input
                    type="number"
                    min={0}
                    value={meetingForm.noticeDaysRequired}
                    onChange={(e) =>
                      setMeetingForm({ ...meetingForm, noticeDaysRequired: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Location</label>
                  <input
                    value={meetingForm.location}
                    onChange={(e) => setMeetingForm({ ...meetingForm, location: e.target.value })}
                  />
                </div>
                <div className="compliance-form-group compliance-form-group--full">
                  <label>Agenda URL</label>
                  <input
                    value={meetingForm.agendaUrl}
                    onChange={(e) => setMeetingForm({ ...meetingForm, agendaUrl: e.target.value })}
                  />
                </div>
              </div>
              <div className="compliance-toolbar" style={{ marginTop: "0.75rem" }}>
                <button type="submit" className="compliance-btn" disabled={saving}>Save</button>
                <button type="button" className="compliance-btn compliance-btn--secondary" onClick={() => setShowMeetingForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showElectionForm ? (
        <div className="compliance-modal-overlay" onClick={() => setShowElectionForm(false)}>
          <div className="compliance-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Plan election</h2>
            <form onSubmit={handleSaveElection}>
              <div className="compliance-form-grid">
                <div className="compliance-form-group compliance-form-group--full">
                  <label>Title</label>
                  <input
                    value={electionForm.title}
                    onChange={(e) => setElectionForm({ ...electionForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Election date</label>
                  <input
                    type="datetime-local"
                    value={electionForm.electionDate}
                    onChange={(e) => setElectionForm({ ...electionForm, electionDate: e.target.value })}
                    required
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Nomination deadline</label>
                  <input
                    type="datetime-local"
                    value={electionForm.nominationDeadline}
                    onChange={(e) => setElectionForm({ ...electionForm, nominationDeadline: e.target.value })}
                  />
                </div>
                <div className="compliance-form-group">
                  <label>Ballot method</label>
                  <select
                    value={electionForm.ballotMethod}
                    onChange={(e) => setElectionForm({ ...electionForm, ballotMethod: e.target.value })}
                  >
                    <option value="in_person">In person at meeting</option>
                    <option value="mail">Mail ballot</option>
                    <option value="poll">Online poll</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="compliance-form-group">
                  <label>Status</label>
                  <select
                    value={electionForm.status}
                    onChange={(e) => setElectionForm({ ...electionForm, status: e.target.value })}
                  >
                    {Object.entries(ELECTION_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="compliance-form-group compliance-form-group--full">
                  <label>Positions (one per line: Title | term years)</label>
                  <textarea
                    rows={4}
                    placeholder={"President | 2\nTreasurer | 2"}
                    value={electionForm.positionsText}
                    onChange={(e) => setElectionForm({ ...electionForm, positionsText: e.target.value })}
                  />
                </div>
              </div>
              <div className="compliance-toolbar" style={{ marginTop: "0.75rem" }}>
                <button type="submit" className="compliance-btn" disabled={saving}>Save</button>
                <button type="button" className="compliance-btn compliance-btn--secondary" onClick={() => setShowElectionForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminCompliancePage;